import PropTypes from 'prop-types';
import React from 'react';
import { useTheme } from 'styled-components';
import MessagePreview from '../MessagePreview';
import {
  ReviewContainer,
  SummaryHeader,
  SummaryCustomer,
  CustomerAvatar,
  CustomerInfo,
  CustomerName,
  CustomerContact,
  SummaryAmount,
  AmountLabel,
  AmountValue,
  TwoColumnLayout,
  LeftColumn,
  SummarySection,
  SummaryList,
  SummaryItem,
  ItemLabel,
  ItemValue,
  getInitials,
} from '../shared/ReviewStyledComponents';

// Helper function to get text based on adjustment type
const getAdjustmentText = (type, key) => {
  const texts = {
    credit: {
      amountLabel: 'Credit Amount',
      sectionTitle: 'Credit Information',
      toIssue: 'Credit to Issue',
    },
    refund: {
      amountLabel: 'Refund Amount',
      sectionTitle: 'Refund Information',
      toIssue: 'Refund to Issue',
    },
  };
  return texts[type]?.[key] || texts.credit[key];
};

const Step3Review = ({
  creditData,
  trackName = 'Your Track',
  type = 'credit',
}) => {
  const theme = useTheme();
  // Calculate new balance
  const newBalance =
    (parseFloat(creditData.currentBalance) || 0) +
    (parseFloat(creditData.amount) || 0);

  // Format expiration date
  const formatExpiration = () => {
    if (!creditData.expiration) return 'No expiration';
    const date = new Date(creditData.expiration);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Replace variables in message
  const replaceVariables = (text) => {
    if (!text) return text;

    return text
      .replace(/\{\{first_name\}\}/g, creditData.customerName.split(' ')[0])
      .replace(/\{\{credit_amount\}\}/g, `$${creditData.amount || '0'}`)
      .replace(/\{\{expiration\}\}/g, formatExpiration())
      .replace(/\{\{track_name\}\}/g, trackName);
  };

  // Calculate character count
  const characterCount = creditData.messageContent
    ? creditData.messageContent.length
    : 0;
  const characterLimit = creditData.messageChannel === 'SMS' ? 160 : 250;

  return (
    <ReviewContainer>
      <SummaryHeader>
        <SummaryCustomer>
          <CustomerAvatar>
            {getInitials(creditData.customerName)}
          </CustomerAvatar>
          <CustomerInfo>
            <CustomerName>{creditData.customerName}</CustomerName>
            <CustomerContact>
              {creditData.customerEmail} • {creditData.customerPhone}
            </CustomerContact>
          </CustomerInfo>
        </SummaryCustomer>
        <SummaryAmount>
          <AmountLabel>{getAdjustmentText(type, 'amountLabel')}</AmountLabel>
          <AmountValue>${creditData.amount || '0.00'}</AmountValue>
        </SummaryAmount>
      </SummaryHeader>

      <TwoColumnLayout>
        <LeftColumn>
          <SummarySection
            title={
              <>
                <svg
                  width="16"
                  height="16"
                  fill={theme?.colors?.primary || '#fa4616'}
                  viewBox="0 0 16 16"
                  style={{ marginRight: '8px' }}
                >
                  <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v5H0V4zm0 6v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2H0zm3 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                </svg>
                {getAdjustmentText(type, 'sectionTitle')}
              </>
            }
          >
            <SummaryList>
              <SummaryItem>
                <ItemLabel>Current Balance</ItemLabel>
                <ItemValue>${creditData.currentBalance || '0.00'}</ItemValue>
              </SummaryItem>
              <SummaryItem>
                <ItemLabel>{getAdjustmentText(type, 'toIssue')}</ItemLabel>
                <ItemValue style={{ color: theme?.colors?.success || '#22C55E' }}>
                  +${creditData.amount || '0.00'}
                </ItemValue>
              </SummaryItem>
              <SummaryItem>
                <ItemLabel>New Balance</ItemLabel>
                <ItemValue style={{ color: theme?.colors?.success || '#22C55E', fontWeight: 700 }}>
                  ${newBalance.toFixed(2)}
                </ItemValue>
              </SummaryItem>
              <SummaryItem>
                <ItemLabel>Expiration Date</ItemLabel>
                <ItemValue>{formatExpiration()}</ItemValue>
              </SummaryItem>
              {creditData.reason && (
                <SummaryItem>
                  <ItemLabel>Reason</ItemLabel>
                  <ItemValue>{creditData.reason}</ItemValue>
                </SummaryItem>
              )}
            </SummaryList>
          </SummarySection>

          {creditData.sendMessage && (
            <SummarySection
              title={
                <>
                  <svg
                    width="16"
                    height="16"
                    fill={theme?.colors?.primary || '#fa4616'}
                    viewBox="0 0 16 16"
                    style={{ marginRight: '8px' }}
                  >
                    <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.5a1 1 0 0 0-.8.4l-1.9 2.533a1 1 0 0 1-1.6 0L5.3 12.4a1 1 0 0 0-.8-.4H2a2 2 0 0 1-2-2V2zm3.5 1a.5.5 0 0 0 0 1h9a.5.5 0 0 0 0-1h-9zm0 2.5a.5.5 0 0 0 0 1h9a.5.5 0 0 0 0-1h-9zm0 2.5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5z" />
                  </svg>
                  Customer Notification
                </>
              }
            >
              <SummaryList>
                <SummaryItem>
                  <ItemLabel>Send Via</ItemLabel>
                  <ItemValue>{creditData.messageChannel} Message</ItemValue>
                </SummaryItem>
                <SummaryItem>
                  <ItemLabel>
                    {creditData.messageChannel === 'SMS'
                      ? 'Phone Number'
                      : 'Device'}
                  </ItemLabel>
                  <ItemValue>
                    {creditData.messageChannel === 'SMS'
                      ? creditData.customerPhone
                      : 'Mobile App'}
                  </ItemValue>
                </SummaryItem>
                <SummaryItem>
                  <ItemLabel>Delivery</ItemLabel>
                  <ItemValue>
                    {creditData.scheduledDateTime
                      ? new Date(creditData.scheduledDateTime).toLocaleString()
                      : 'Immediate'}
                  </ItemValue>
                </SummaryItem>
                <SummaryItem>
                  <ItemLabel>Character Count</ItemLabel>
                  <ItemValue>
                    {characterCount} / {characterLimit}
                  </ItemValue>
                </SummaryItem>
              </SummaryList>
            </SummarySection>
          )}
        </LeftColumn>

        {creditData.sendMessage && (
          <SummarySection
            title={
              <>
                <svg
                  width="16"
                  height="16"
                  fill={theme?.colors?.primary || '#fa4616'}
                  viewBox="0 0 16 16"
                  style={{ marginRight: '8px' }}
                >
                  <path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z" />
                  <path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8zm8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z" />
                </svg>
                Message Preview
              </>
            }
          >
            <MessagePreview
              channel={creditData.messageChannel}
              message={replaceVariables(creditData.messageContent)}
              pushTitle={replaceVariables(creditData.messageTitle)}
              showTitle={false}
              compact={true}
            />
          </SummarySection>
        )}
      </TwoColumnLayout>
    </ReviewContainer>
  );
};

Step3Review.propTypes = {
  creditData: PropTypes.object.isRequired,
  trackName: PropTypes.string,
  type: PropTypes.oneOf(['credit', 'refund']),
};

export default Step3Review;
